import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Input, Button, useDivisionMode } from "../components/ui";
import {
  useLiveFleet,
  driverProgress,
  type LiveVehicle,
  type PodEvent,
} from "../features/lastmile/useLiveFleet";

// FleetPage — "Vehículos en ruta". Layout inspirado en DispatchTrack:
//   - Header global con KPIs agregados (cumplimiento, asignadas,
//     gestionadas, entregadas %, parciales %, no entregadas %).
//   - Una card por vehículo en 2 columnas:
//       Izquierda (compacta, ~300px): placa + chofer + contadores +
//       breakdown vertical de entregadas/parciales/no entregadas y
//       barra de cumplimiento.
//       Derecha (extendida): timeline horizontal con eje X de horas
//       (06–21). Un círculo por POD del día, coloreado según resultado:
//       verde = entregada, ámbar = parcial, rojo = rechazada.
//
// La idea: el supervisor ve de un vistazo a qué hora trabajó cada
// vehículo durante el día sin tener que abrir el detalle.

export function FleetPage() {
  // El modo (CONSUMO o SALUD) lo controla el sidebar. La página renderiza
  // el mismo layout en ambos modos — solo cambia la fuente de datos que
  // pasamos al hook. Para SALUD, el `useLiveFleet` ignora `plannedDate`
  // y trae las rutas self-dispatch de los últimos 3 días.
  const [division] = useDivisionMode();
  // Hoy en LOCAL — el filter del backend depende de planned_date que se
  // guarda en zona Lima. Evita drift UTC que hacía que la lista quedara
  // vacía entre las 19:00 y la medianoche.
  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const { vehicles, loading, error, lastRefresh, refresh } = useLiveFleet(
    20_000,
    todayISO,
    undefined,
    division,
  );
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = !q
      ? vehicles
      : vehicles.filter(
          (v) =>
            v.plate.toLowerCase().includes(q) ||
            (v.driverFullName ?? "").toLowerCase().includes(q) ||
            (v.driverCode ?? "").toLowerCase().includes(q),
        );
    // Orden principal: cumplimiento descendente (mismo KPI que pinta la
    // barra de cada card: entregadas / asignadas). El supervisor abre la
    // pantalla y los choferes más adelantados quedan arriba — ve de un
    // vistazo quién va bien y quién necesita ayuda al final.
    //
    // Vehículos sin rutas (stopsTotal=0) caen al final con cumplimiento=
    // -1 sentinel para no mezclarse con los que tienen 0% por mal día.
    // Desempate estable por placa para que el orden no salte entre polls.
    const cumplimiento = (v: LiveVehicle): number => {
      if (v.stopsTotal === 0) return -1;
      return v.stopsDone / v.stopsTotal;
    };
    return [...filtered].sort((a, b) => {
      const ca = cumplimiento(a);
      const cb = cumplimiento(b);
      if (cb !== ca) return cb - ca;
      return a.plate.localeCompare(b.plate);
    });
  }, [vehicles, query]);

  const totals = useMemo(() => buildTotals(vehicles), [vehicles]);

  if (loading && vehicles.length === 0) {
    return <CenteredMsg title="Cargando flota…" />;
  }
  if (error && vehicles.length === 0) {
    return (
      <CenteredMsg title="No se pudo cargar la flota">
        <p className="text-sm text-sub mb-3">{error}</p>
        <Button variant="secondary" size="sm" onClick={refresh}>Reintentar</Button>
      </CenteredMsg>
    );
  }
  if (vehicles.length === 0) {
    return (
      <CenteredMsg title="Sin rutas en curso">
        <p className="text-sm text-sub mb-3">
          Importá la planificación de última milla o programá rutas para verlas acá.
        </p>
        <Link
          to="/ultima-milla/importar"
          className="inline-block bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded-md px-3 py-1.5"
        >
          Importar planificación
        </Link>
      </CenteredMsg>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-3">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-ink">
            {visible.length} vehículo{visible.length === 1 ? "" : "s"} en ruta
          </h1>
          <p className="text-xs text-sub mt-0.5">
            {lastRefresh
              ? `Actualizado ${lastRefresh.toLocaleTimeString("es-PE")} · refresca cada 20s`
              : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar placa o chofer…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-64"
          />
          <Button variant="secondary" size="sm" onClick={refresh}>
            Refrescar
          </Button>
        </div>
      </header>

      {/* Strip de KPIs agregados de toda la flota — siempre visible
          arriba para que el supervisor tenga un pulso del día sin
          mirar card por card. */}
      <KpisStrip totals={totals} />

      <div className="space-y-2">
        {visible.map((v) => (
          <FleetTimelineRow key={v.id} vehicle={v} />
        ))}
        {visible.length === 0 && (
          <Card padding="lg" className="text-center text-sub text-sm">
            No hay vehículos que coincidan con la búsqueda.
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// KPIs globales (strip horizontal arriba)
// ---------------------------------------------------------------------

type Totals = {
  asignadas: number;
  gestionadas: number;
  entregadas: number;
  parciales: number;
  noEntregadas: number;
};

function buildTotals(vehicles: LiveVehicle[]): Totals {
  let asignadas = 0, entregadas = 0, parciales = 0, noEntregadas = 0;
  let podDelivered = 0, podPartial = 0;
  for (const v of vehicles) {
    asignadas += v.stopsTotal;
    // Eventos POD: separamos delivered "puro" de partial para
    // poder mostrar % parciales aparte (matchea con la imagen ref).
    for (const ev of v.podEvents) {
      if (ev.kind === "delivered") podDelivered++;
      else if (ev.kind === "partial") podPartial++;
    }
    parciales += v.podEvents.filter((e) => e.kind === "partial").length;
    entregadas += v.podEvents.filter((e) => e.kind === "delivered").length;
    noEntregadas += v.stopsFailed;
  }
  void podDelivered; void podPartial;
  const gestionadas = entregadas + parciales + noEntregadas;
  return { asignadas, gestionadas, entregadas, parciales, noEntregadas };
}

function KpisStrip({ totals }: { totals: Totals }) {
  const { asignadas, gestionadas, entregadas, parciales, noEntregadas } = totals;
  const cumplimientoTotal = asignadas === 0 ? 0 : (entregadas / asignadas) * 100;
  const pctEntregadas = asignadas === 0 ? 0 : (entregadas / asignadas) * 100;
  const pctParciales = asignadas === 0 ? 0 : (parciales / asignadas) * 100;
  const pctNoEntregadas = asignadas === 0 ? 0 : (noEntregadas / asignadas) * 100;
  return (
    <Card padding="none" className="px-4 py-2.5">
      <div className="flex items-center gap-6 flex-wrap text-sm">
        <KpiCell value={`${cumplimientoTotal.toFixed(1)}%`} label="Cumplimiento total" accent="#2E7D32" />
        <KpiCell value={String(asignadas)} label="Asignadas" />
        <KpiCell value={String(gestionadas)} label="Gestionadas" />
        <KpiCell value={`${pctEntregadas.toFixed(1)}%`} label="Entregas" accent="#2E7D32" />
        <KpiCell value={`${pctParciales.toFixed(1)}%`} label="Parciales" accent="#F9A825" />
        <KpiCell value={`${pctNoEntregadas.toFixed(1)}%`} label="No Entregadas" accent="#C62828" />
      </div>
    </Card>
  );
}

function KpiCell({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span
        className="text-base font-bold tabular-nums"
        style={{ color: accent ?? "var(--ink, #243B53)" }}
      >
        {value}
      </span>
      <span className="text-xs text-sub">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------
// Card por vehículo con timeline horizontal
// ---------------------------------------------------------------------

const TIMELINE_HOUR_FROM = 6;  // 06:00
const TIMELINE_HOUR_TO = 21;   // 21:00 (exclusivo del último tick)
const TIMELINE_TICKS = [9, 12, 15, 18, 21];

function FleetTimelineRow({ vehicle }: { vehicle: LiveVehicle }) {
  const pct = driverProgress(vehicle) * 100;
  const cumplimiento = vehicle.stopsTotal === 0
    ? 0
    : (vehicle.stopsDone / vehicle.stopsTotal) * 100;
  const gestionadas = vehicle.stopsDone + vehicle.stopsFailed;

  const delivered = vehicle.podEvents.filter((e) => e.kind === "delivered").length;
  const partial = vehicle.podEvents.filter((e) => e.kind === "partial").length;
  const failed = vehicle.podEvents.filter(
    (e) => e.kind === "failed" || e.kind === "skipped",
  ).length;
  const totalDay = vehicle.stopsTotal || 1;

  const cumplimientoAccent =
    cumplimiento >= 80 ? "#2E7D32" : cumplimiento >= 40 ? "#F9A825" : "#C62828";

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex">
        {/* Panel izquierdo — info compacta del vehículo */}
        <div className="w-[320px] shrink-0 border-r border-border p-3 space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${pct > 0 ? "bg-state-delivered" : "bg-sub"}`}
              />
              <span className="font-mono font-semibold text-ink">
                {vehicle.plate}
              </span>
            </div>
            <div className="text-xs text-sub mt-1 truncate">
              {vehicle.driverFullName ?? (
                <span className="italic">Sin chofer asignado</span>
              )}
            </div>
            {vehicle.driverPhone && (
              <div className="text-[11px] text-sub font-mono mt-0.5">
                {vehicle.driverPhone}
              </div>
            )}
          </div>

          {/* 3 métricas inline: asignadas / entregas / gestionadas */}
          <div className="flex items-baseline justify-between gap-2">
            <Mini value={vehicle.stopsTotal} label="Asignadas" />
            <Mini value={vehicle.stopsDone} label="Entregas" accent="#2E7D32" />
            <Mini value={gestionadas} label="Gestionadas" />
          </div>

          {/* Breakdown vertical con barra y % */}
          <div className="space-y-1.5 pt-1">
            <BreakdownRow
              count={delivered}
              total={totalDay}
              label="Entregadas"
              color="#2E7D32"
            />
            <BreakdownRow
              count={partial}
              total={totalDay}
              label="Parciales"
              color="#F9A825"
            />
            <BreakdownRow
              count={failed}
              total={totalDay}
              label="No Entregadas"
              color="#C62828"
            />
          </div>
        </div>

        {/* Panel derecho — timeline horizontal */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-center justify-end mb-1.5">
            <span className="text-xs text-sub mr-2">Cumplimiento</span>
            <div className="w-32 h-1 rounded-full bg-page overflow-hidden">
              <div
                className="h-full transition-[width]"
                style={{
                  width: `${Math.min(cumplimiento, 100)}%`,
                  backgroundColor: cumplimientoAccent,
                }}
              />
            </div>
            <span
              className="ml-2 text-sm font-bold tabular-nums"
              style={{ color: cumplimientoAccent }}
            >
              {cumplimiento.toFixed(0)}%
            </span>
          </div>
          <Timeline events={vehicle.podEvents} />
        </div>
      </div>
    </Card>
  );
}

function Mini({
  value,
  label,
  accent,
}: {
  value: number;
  label: string;
  accent?: string;
}) {
  return (
    <div className="text-center">
      <div
        className="text-base font-bold tabular-nums"
        style={{ color: accent ?? "var(--ink, #243B53)" }}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-sub leading-tight">
        {label}
      </div>
    </div>
  );
}

function BreakdownRow({
  count,
  total,
  label,
  color,
}: {
  count: number;
  total: number;
  label: string;
  color: string;
}) {
  const pct = total === 0 ? 0 : (count / total) * 100;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-sub mb-0.5">
        <span>
          <span className="font-semibold tabular-nums text-ink">{count}</span>{" "}
          {label}
        </span>
        <span className="font-mono">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-1 rounded-full bg-page overflow-hidden">
        <div
          className="h-full"
          style={{
            width: `${Math.min(pct, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

/// Timeline horizontal: eje X de horas (06–21) con ticks cada 3h. Cada
/// PodEvent se posiciona en su hora exacta y se pinta como un círculo
/// del color según el resultado.
function Timeline({ events }: { events: PodEvent[] }) {
  const fromMin = TIMELINE_HOUR_FROM * 60;
  const toMin = TIMELINE_HOUR_TO * 60;
  const range = toMin - fromMin;

  const xFor = (d: Date): number => {
    const local = d.toLocaleString("en-US", {
      timeZone: "America/Lima",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
    // local viene como "HH:MM" o "HH:MM, ..." según locale; tomamos
    // las 5 primeras chars que es HH:MM siempre.
    const [hh, mm] = local.slice(0, 5).split(":").map((n) => parseInt(n, 10));
    const totalMin = hh * 60 + mm;
    if (totalMin < fromMin) return 0;
    if (totalMin > toMin) return 100;
    return ((totalMin - fromMin) / range) * 100;
  };

  return (
    <div className="relative h-12">
      {/* Línea base del eje */}
      <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
      {/* Ticks (texto de hora alineado al porcentaje) */}
      {TIMELINE_TICKS.map((h) => {
        const x = ((h * 60 - fromMin) / range) * 100;
        return (
          <div
            key={h}
            className="absolute text-[10px] text-sub"
            style={{
              left: `${x}%`,
              top: "100%",
              transform: "translate(-50%, -100%)",
            }}
          >
            {String(h).padStart(2, "0")}:00
          </div>
        );
      })}
      {/* Círculos por POD event */}
      {events.map((ev, i) => {
        const x = xFor(ev.at);
        const color =
          ev.kind === "delivered"
            ? "#2E7D32"
            : ev.kind === "partial"
              ? "#F9A825"
              : "#C62828";
        return (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full border border-white shadow-sm"
            style={{
              left: `${x}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: color,
            }}
            title={`${ev.kind} · ${ev.at.toLocaleTimeString("es-PE", { hour12: false })}`}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------

function CenteredMsg({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card padding="lg" className="max-w-md text-center">
        <h2 className="text-base text-ink font-semibold mb-2">{title}</h2>
        {children}
      </Card>
    </div>
  );
}
